import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, Lock, FilePlus2, ExternalLink, ArrowLeft, Download, Mail, Search, Pencil, Ban } from "lucide-react";
import { api, apiErr, money, fmtDate } from "./lib/api";
import { useAuth } from "./auth";
import { Card, Btn, Input, Label, Select, PageHeader, Badge, StatusBadge, Modal, Th, Td, ClientPicker, Pagination } from "./ui";
import { ExcelButton, InvoiceTotalsBar, SortToggle, NewBadge, DateFyWarning, useUrlState, useUrlNumber } from "./totals";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "AED"];
const PAGE = 50;

export function Invoices() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState(null);
  const [skip, setSkip] = useUrlNumber("skip", 0);
  const [invNo, setInvNo] = useUrlState("inv_no");
  const [client, setClient] = useUrlState("client");
  const [item, setItem] = useUrlState("item");
  const [searchIn, setSearchIn] = useUrlState("in", "all");
  const [q, setQ] = useUrlState("q");
  const [dateFrom, setDateFrom] = useUrlState("from");
  const [dateTo, setDateTo] = useUrlState("to");
  const [fy, setFy] = useUrlState("fy");
  const [sort, setSort] = useUrlState("sort", "date");
  const [sp, setSp] = useSearchParams();
  const aging = sp.get("aging") || "";
  const pay = sp.get("pay") || "";
  const nav = useNavigate();
  const searchParam = searchIn === "all" ? { ...(q ? { q } : {}) } : searchIn === "inv_no" ? { ...(q ? { inv_no: q } : {}) } : searchIn === "client" ? { ...(q ? { client: q } : {}) } : { ...(q ? { item: q } : {}) };
  const filterParams = {
    ...(aging ? { aging } : {}), ...(pay ? { pay } : {}), ...searchParam,
    ...(invNo ? { inv_no: invNo } : {}), ...(client ? { client } : {}), ...(item ? { item } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}), ...(dateTo ? { date_to: dateTo } : {}), ...(fy ? { fy } : {}),
  };
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/invoices", { params: { skip, limit: PAGE, sort, ...filterParams } })
        .then((r) => { setRows(r.data.items); setTotal(r.data.total); setTotals(r.data.totals); })
        .catch((e) => toast.error(apiErr(e)));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invNo, client, item, q, searchIn, dateFrom, dateTo, fy, skip, aging, pay, sort]);
  const clearFilters = () => setSp({}, { replace: true });
  const statusOf = (i) => i.balance <= 0 && i.total > 0 ? "paid" : i.paid_amount > 0 ? "partial" : i.status;
  const setPay = (v) => { const n = new URLSearchParams(window.location.search); if (v) n.set("pay", v); else n.delete("pay"); n.delete("skip"); setSp(n, { replace: true }); };
  return (
    <div className="animate-fade-up">
      <PageHeader title="Invoices" subtitle={`${total} invoice${total !== 1 ? "s" : ""} · Infomedia Web Solutions`}
        actions={<div className="flex gap-2"><ExcelButton path="/invoices/export.xlsx" params={filterParams} testid="invoices-excel-btn" />
          <Btn data-testid="new-invoice-btn" onClick={() => nav("/invoices/new")}><FilePlus2 size={16} /> New Invoice</Btn></div>} />
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <Label>Search in</Label>
          <Select data-testid="filter-search-in" className="w-40" value={searchIn} onChange={(e) => { setSearchIn(e.target.value); setSkip(0); }}>
            <option value="all">All fields</option><option value="inv_no">Invoice No</option><option value="client">Client</option><option value="item">Item / Particulars</option>
          </Select>
        </div>
        <div className="flex-1 min-w-[260px] max-w-xl">
          <Label>Search</Label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="filter-search" className="pl-9" placeholder={searchIn === "item" ? "e.g. hosting, domain renewal, SEO…" : searchIn === "inv_no" ? "e.g. 255 or IWS/2026-27/255" : searchIn === "client" ? "Client name" : "Invoice no, client or item text"}
              value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} />
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[["", "All"], ["short", "Short Paid"], ["unpaid", "Unpaid"], ["paid", "Paid"]].map(([v, l]) => (
            <button key={v || "allpay"} data-testid={`filter-pay-${v || "all"}`} onClick={() => setPay(v)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${pay === v ? (v === "short" ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-sky-500/20 text-sky-300 border-sky-500/40") : "bg-slate-800/40 text-slate-400 border-slate-700 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label>Invoice #</Label>
          <Input data-testid="filter-invoice-no" className="w-36" placeholder="e.g. 255"
            value={invNo} onChange={(e) => { setInvNo(e.target.value); setSkip(0); }} />
        </div>
        <div>
          <Label>Client</Label>
          <Input data-testid="filter-client" className="w-44" placeholder="Client name"
            value={client} onChange={(e) => { setClient(e.target.value); setSkip(0); }} />
        </div>
        <div>
          <Label>Item</Label>
          <Input data-testid="filter-item" className="w-44" placeholder="Particulars text"
            value={item} onChange={(e) => { setItem(e.target.value); setSkip(0); }} />
        </div>
        <div>
          <Label>From</Label>
          <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }} />
        </div>
        <div>
          <Label>To</Label>
          <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setSkip(0); }} />
        </div>
        <div className="flex items-center gap-1.5">
          {[["", "All"], ["this", "This FY"], ["last", "Last FY"]].map(([v, l]) => (
            <button key={v || "all"} data-testid={`filter-fy-${v || "all"}`}
              onClick={() => { setFy(v); setSkip(0); }}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${fy === v ? "bg-sky-500/20 text-sky-300 border-sky-500/40" : "bg-slate-800/40 text-slate-400 border-slate-700 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
        <SortToggle sort={sort} setSort={(v) => { setSort(v); setSkip(0); }} testid="inv-sort" />
        {(invNo || client || item || q || dateFrom || dateTo || fy || pay) && (
          <Btn variant="ghost" data-testid="filter-clear" onClick={clearFilters}>Clear</Btn>
        )}
        {aging && (
          <button data-testid="aging-filter-chip" onClick={() => setSp({})}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20">
            Aging: {aging} days overdue <span className="text-amber-400">✕</span>
          </button>
        )}
      </div>
      <Card className="overflow-x-auto md:overflow-visible">
        <table className="w-full min-w-[760px]">
          <thead className="bg-slate-900/40"><tr><Th>Invoice #</Th><Th>Client</Th><Th>Date</Th><Th>Total</Th><Th>Paid</Th><Th>Balance</Th><Th>Status</Th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="border-t border-slate-800/60 hover:bg-slate-800/30 cursor-pointer" data-testid={`invoice-row-${i.id}`} onClick={() => nav(`/invoices/${i.id}`)}>
                <Td className="font-mono"><a href={`/invoices/${i.id}`} onClick={(e) => { if (e.ctrlKey || e.metaKey || e.button === 1) e.stopPropagation(); else e.preventDefault(); }} className="hover:text-sky-300">{i.invoice_number}</a><NewBadge createdAt={i.created_at} />
                  <a href={`/invoices/${i.id}`} target="_blank" rel="noreferrer" title="Open in new tab" data-testid={`open-new-tab-${i.id}`} onClick={(e) => e.stopPropagation()} className="ml-2 inline-block align-middle text-slate-600 hover:text-sky-300"><ExternalLink size={12} /></a></Td>
                <Td className="text-slate-300 max-w-[220px] truncate">{i.client_name_legacy || "—"}</Td>
                <Td className="text-slate-400">{fmtDate(i.date)}</Td>
                <Td className="font-mono">{money(i.total, i.currency)}</Td>
                <Td className="font-mono text-emerald-400">{money(i.paid_amount, i.currency)}</Td>
                <Td className="font-mono">{money(i.balance, i.currency)}</Td>
                <Td><div className="flex items-center gap-1.5"><StatusBadge status={statusOf(i)} />{statusOf(i) === "partial" && (i.short_is_tds ? <Badge tone="emerald" data-testid={`tds-flag-${i.id}`}>= TDS</Badge> : <Badge tone="amber" data-testid={`short-flag-${i.id}`}>SHORT</Badge>)}</div></Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td className="text-slate-500">No invoices found.</Td></tr>}
          </tbody>
        </table>
        <InvoiceTotalsBar totals={totals} skip={skip} limit={PAGE} total={total} />
        <Pagination skip={skip} limit={PAGE} total={total} onPage={setSkip} />
      </Card>
    </div>
  );
}

const blankItem = { description: "", service_id: "", hsn_sac: "", qty: 1, rate: 0, gst_rate: 18 };

export function InvoiceEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [companies, setCompanies] = useState([]);
  const [services, setServices] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [clientLabel, setClientLabel] = useState("");
  const [invNumber, setInvNumber] = useState("");
  const [refinalize, setRefinalize] = useState(false);
  const [f, setF] = useState({
    company_id: "", client_id: "", date: new Date().toISOString().slice(0, 10), due_date: "",
    currency: "INR", exchange_rate: 1, supply_type: "intra", items: [{ ...blankItem }], notes: "", recurring_type: "", renewal_date: "",
  });
  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!isEdit) {
        const primary = r.data.find((c) => c.is_primary) || r.data[0];
        if (primary) setF((s) => ({ ...s, company_id: primary.id }));
      }
    });
    api.get("/services").then((r) => setServices(r.data));
    api.get("/tax-rates").then((r) => setTaxRates(r.data));
    if (isEdit) {
      const loadForEdit = async () => {
        let d = (await api.get(`/invoices/${id}`)).data;
        if (d.is_locked) {
          try { await api.post(`/invoices/${id}/revise`); d = (await api.get(`/invoices/${id}`)).data; setRefinalize(true); toast.info(`Editing finalised invoice ${d.invoice_number} — it will be re-finalised when you save`); }
          catch (e) { toast.error(apiErr(e)); nav(`/invoices/${id}`); return; }
        } else if ((d.version || 1) > 1) setRefinalize(true);
        return d;
      };
      loadForEdit().then((d) => {
        if (!d) return;
        setInvNumber(d.invoice_number);
        setClientLabel(d.client_name_legacy || "");
        if (d.client_id) api.get(`/clients/${d.client_id}`).then((cr) => setClientLabel(cr.data.name)).catch(() => {});
        setF({
          company_id: d.company_id || "", client_id: d.client_id || "", date: (d.date || "").slice(0, 10),
          due_date: (d.due_date || "").slice(0, 10), currency: d.currency || "INR", exchange_rate: d.exchange_rate || 1,
          supply_type: d.supply_type || "intra", notes: d.notes || "", recurring_type: d.recurring ? (d.recurring_type || "") : "", renewal_date: d.renewal_date || "",
          items: (d.items || []).map((it) => ({ description: it.description || "", service_id: "", hsn_sac: it.hsn_sac || "", qty: it.qty ?? 1, rate: it.rate ?? 0, gst_rate: it.gst_rate ?? 18 })),
        });
      }).catch((e) => toast.error(apiErr(e)));
    }
  }, [id, isEdit, nav]);

  const gstOptions = useMemo(() => {
    const set = new Set([0, ...taxRates.map((t) => t.rate)]);
    return [...set].sort((a, b) => a - b);
  }, [taxRates]);

  const calc = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    f.items.forEach((it) => {
      const amt = (Number(it.qty) || 0) * (Number(it.rate) || 0);
      sub += amt;
      const tax = f.supply_type === "export" ? 0 : (amt * (Number(it.gst_rate) || 0)) / 100;
      if (f.supply_type === "intra") { cgst += tax / 2; sgst += tax / 2; } else if (f.supply_type === "inter") { igst += tax; }
    });
    if (f.supply_type !== "export") { cgst = Math.round(cgst); sgst = Math.round(sgst); igst = Math.round(igst); }
    const exact = Math.round((sub + cgst + sgst + igst) * 100) / 100;
    const total = f.supply_type === "export" ? exact : Math.round(exact);
    return { sub, cgst, sgst, igst, total, roundOff: Math.round((total - exact) * 100) / 100 };
  }, [f.items, f.supply_type]);

  const setItem = (idx, key, val) => setF((s) => ({ ...s, items: s.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  const addItem = () => setF((s) => ({ ...s, items: [...s.items, { ...blankItem }] }));
  const delItem = (idx) => setF((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!f.company_id || !f.client_id) return toast.error("Select company and client");
    if (!f.items.length || !f.items[0].description) return toast.error("Add at least one line item");
    try {
      if (f.recurring_type === "Renewal" && !f.renewal_date) return toast.error("Enter the renewal date");
      const body = { ...f, recurring: Boolean(f.recurring_type), items: f.items.map((it) => ({ description: it.description, hsn_sac: it.hsn_sac, qty: Number(it.qty), rate: Number(it.rate), gst_rate: Number(it.gst_rate) })) };
      if (isEdit) {
        const { data } = await api.patch(`/invoices/${id}`, body);
        if (refinalize) { await api.post(`/invoices/${id}/finalize`); toast.success(`Updated & re-finalised ${data.invoice_number}`); }
        else toast.success(`Updated ${data.invoice_number}`);
        nav(`/invoices/${id}`);
      } else {
        const { data } = await api.post("/invoices", body);
        toast.success(`Created ${data.invoice_number}`);
        nav(`/invoices/${data.id}`);
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={isEdit ? `Edit ${invNumber === "PREVIEW" ? "Preview" : invNumber}` : "New GST Invoice"} subtitle="Line items, HSN/SAC and automatic CGST/SGST/IGST · invoice number is assigned when you Finalise"
        actions={<div className="flex gap-2"><Btn variant="ghost" onClick={() => nav(isEdit ? `/invoices/${id}` : "/invoices")}><ArrowLeft size={16} /> Back</Btn>
          <Btn data-testid="save-invoice-btn" onClick={save}><Save size={16} /> {isEdit ? "Save Changes" : "Save Invoice"}</Btn></div>} />
      <div className="space-y-6">
        <Card className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div><Label>Company</Label><Select data-testid="inv-company" value={f.company_id} onChange={(e) => setF({ ...f, company_id: e.target.value })}>
              <option value="">Select…</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></div>
            <div><Label>Client</Label>
              <ClientPicker testid="inv-client" selectedLabel={clientLabel}
                onSelect={(c) => { setF((s) => ({ ...s, client_id: c.id, supply_type: (c.state_code && c.state_code !== "7") ? "inter" : s.supply_type })); setClientLabel(c.name); }} /></div>
            <div><Label>Supply Type</Label><Select data-testid="inv-supply" value={f.supply_type} onChange={(e) => setF({ ...f, supply_type: e.target.value })}>
              <option value="intra">Intra-state (CGST+SGST)</option><option value="inter">Inter-state (IGST)</option><option value="export">Export / Zero-rated</option></Select></div>
            <div><Label>Invoice Date</Label><Input data-testid="inv-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />{!isEdit && <DateFyWarning date={f.date} invNumber={invNumber} />}</div>
            <div><Label>Due Date</Label><Input data-testid="inv-due" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
            <div><Label>Currency</Label><Select data-testid="inv-currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}</Select></div>
            <div><Label>Recurring</Label><Select data-testid="inv-recurring" value={f.recurring_type} onChange={(e) => setF({ ...f, recurring_type: e.target.value, renewal_date: e.target.value === "Renewal" ? f.renewal_date : "" })}>
              <option value="">Not recurring</option><option value="Monthly">Monthly</option><option value="Quarterly">Quarterly</option>
              <option value="Bimonthly">Half-yearly (Bi-monthly)</option><option value="Annual">Annual</option><option value="Renewal">Renewal by date</option></Select></div>
            {f.recurring_type === "Renewal" && <div><Label>Renewal Date</Label><Input data-testid="inv-renewal-date" type="date" value={f.renewal_date} onChange={(e) => setF({ ...f, renewal_date: e.target.value })} /></div>}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-slate-200">Line Items</h3>
            <Btn data-testid="add-item-btn" variant="ghost" className="px-3 py-1.5 text-xs" onClick={addItem}><Plus size={14} /> Add line</Btn>
          </div>
          <div className="overflow-x-auto md:overflow-visible">
            <table className="w-full min-w-[900px] table-fixed">
              <colgroup>
                <col style={{ width: "30%" }} /><col style={{ width: "20%" }} /><col style={{ width: "11%" }} />
                <col style={{ width: "8%" }} /><col style={{ width: "12%" }} /><col style={{ width: "9%" }} />
                <col style={{ width: "12%" }} /><col style={{ width: "44px" }} />
              </colgroup>
              <thead><tr><Th>Description</Th><Th>Service Type</Th><Th>HSN/SAC</Th><Th>Qty</Th><Th>Rate</Th><Th>GST%</Th><Th className="text-right">Amount</Th><Th></Th></tr></thead>
              <tbody>
                {f.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-800/60 align-top">
                    <Td><textarea data-testid={`item-desc-${idx}`} rows={2}
                      className="w-full bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-sky-500 resize-y"
                      value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Item / service description" /></Td>
                    <Td><Select data-testid={`item-service-${idx}`} className="w-full text-xs" value={it.service_id || ""}
                      onChange={(e) => {
                        const svc = services.find((s) => s.id === e.target.value);
                        setF((s) => ({ ...s, items: s.items.map((x, i) => i === idx ? {
                          ...x, service_id: e.target.value,
                          hsn_sac: svc ? (svc.sac_code || x.hsn_sac) : x.hsn_sac,
                          description: x.description || (svc ? svc.name : ""),
                        } : x) }));
                      }}>
                      <option value="">— select —</option>
                      {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.sac_code ? ` (${s.sac_code})` : ""}</option>)}</Select></Td>
                    <Td><Input data-testid={`item-hsn-${idx}`} className="w-full font-mono text-xs" value={it.hsn_sac} onChange={(e) => setItem(idx, "hsn_sac", e.target.value)} /></Td>
                    <Td><Input data-testid={`item-qty-${idx}`} className="w-full font-mono" type="number" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} /></Td>
                    <Td><Input data-testid={`item-rate-${idx}`} className="w-full font-mono" type="number" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} /></Td>
                    <Td><Select data-testid={`item-gst-${idx}`} className="w-full font-mono" value={it.gst_rate} onChange={(e) => setItem(idx, "gst_rate", e.target.value)}>
                      {gstOptions.map((g) => <option key={g} value={g}>{g}%</option>)}</Select></Td>
                    <Td className="font-mono text-right whitespace-nowrap">{money((Number(it.qty) || 0) * (Number(it.rate) || 0), f.currency)}</Td>
                    <Td>{f.items.length > 1 && <button data-testid={`del-item-${idx}`} onClick={() => delItem(idx)} className="text-rose-400 hover:text-rose-300"><Trash2 size={16} /></button>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 flex justify-end">
            <div className="w-full max-w-xs font-mono text-sm space-y-2" data-testid="tax-breakdown">
              <Row label="Subtotal" value={money(calc.sub, f.currency)} />
              {f.supply_type === "intra" && <><Row label="CGST" value={money(calc.cgst, f.currency)} /><Row label="SGST" value={money(calc.sgst, f.currency)} /></>}
              {f.supply_type === "inter" && <Row label="IGST" value={money(calc.igst, f.currency)} />}
              {f.supply_type === "export" && <Row label="GST (zero-rated)" value={money(0, f.currency)} />}
              {calc.roundOff !== 0 && <Row label="Round off" value={`${calc.roundOff > 0 ? "+" : "−"}${money(Math.abs(calc.roundOff), f.currency)}`} />}
              <div className="border-t border-slate-700 my-2" />
              <div className="flex justify-between text-lg"><span className="text-slate-300">Total (rounded)</span><span className="text-emerald-400 font-bold">{money(calc.total, f.currency)}</span></div>
            </div>
          </div>
        </Card>

        <Card className="p-5"><Label>Notes</Label>
          <textarea data-testid="inv-notes" className="w-full bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-sky-500" rows={3}
            value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-slate-400"><span>{label}</span><span className="text-slate-200">{value}</span></div>;
}

export function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [inv, setInv] = useState(null);
  const [pay, setPay] = useState(false);
  const [editPayId, setEditPayId] = useState(null);
  const [pf, setPf] = useState({ amount: "", tds: "", date: new Date().toISOString().slice(0, 10), method: "bank_transfer", reference: "", note: "" });
  const [hardDelOpen, setHardDelOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!inv) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`;
  const gstRates = [...new Set((inv.items || []).map((it) => Number(it.gst_rate) || 0))];
  const oneRate = gstRates.length === 1 ? gstRates[0] : null;
  const pct = (v) => `${+Number(v).toFixed(2)}`;
  const sendEmail = async () => {
    if (!emailTo) return toast.error("Enter recipient email");
    setSending(true);
    try { await api.post(`/invoices/${id}/email`, { to: emailTo }); toast.success(`Emailed to ${emailTo}`); setEmailOpen(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const finalize = async () => { try { await api.post(`/invoices/${id}/finalize`); toast.success("Finalized & locked (immutable)"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const addPay = async () => {
    try { await api.post("/payments", { ...pf, amount: Number(pf.amount || 0), tds: Number(pf.tds || 0), invoice_id: id }); toast.success("Payment recorded"); setPay(false); setPf({ ...pf, amount: "", tds: "", reference: "", note: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const startEditPay = (p) => { setEditPayId(p.id); setPf({ amount: String(p.amount ?? ""), tds: String(p.tds ?? ""), date: (p.date || "").slice(0, 10), method: p.method || "bank_transfer", reference: p.reference || "", note: p.note || "" }); };
  const cancelEditPay = () => { setEditPayId(null); setPf({ ...pf, amount: "", tds: "", reference: "", note: "" }); };
  const saveEditPay = async () => {
    try { await api.patch(`/payments/${editPayId}`, { ...pf, amount: Number(pf.amount || 0), tds: Number(pf.tds || 0) }); toast.success("Payment updated"); cancelEditPay(); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deletePay = async (p) => {
    if (!window.confirm(`Delete payment of ${money(p.amount, inv.currency)} dated ${fmtDate(p.date)}? This is logged in the audit trail.`)) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Payment deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const cancelInvoice = async () => {
    try { await api.post(`/invoices/${id}/cancel`); toast.success("Invoice cancelled"); setCancelOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const hardDelete = async () => {
    try { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); nav("/invoices"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const isCancelled = inv.status === "cancelled";
  const isPreview = !inv.is_locked && !isCancelled;

  return (
    <div className="animate-fade-up">
      <PageHeader title={inv.invoice_number === "PREVIEW" ? "Invoice Preview" : inv.invoice_number} subtitle={`${inv.supply_type.toUpperCase()} • ${inv.currency} • v${inv.version}${inv.recurring ? ` • RECURRING: ${inv.recurring_type}${inv.recurring_type === "Renewal" && inv.renewal_date ? ` (${fmtDate(inv.renewal_date)})` : ""}${inv.recurring_status === "done" ? " ✓ billed" : ""}` : ""}`}
        actions={<div className="flex flex-wrap gap-2">
          <Btn variant="ghost" onClick={() => nav("/invoices")}><ArrowLeft size={16} /> Back</Btn>
          {!isCancelled && <Btn data-testid="edit-invoice-btn" variant="ghost" onClick={() => nav(`/invoices/${id}/edit`)}><Pencil size={16} /> Edit</Btn>}
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800/60 hover:bg-slate-700 text-slate-200 border border-slate-700"><Download size={16} /> PDF</a>
          <Btn data-testid="email-invoice-btn" variant="ghost" onClick={() => setEmailOpen(true)}><Mail size={16} /> Email</Btn>
          {!inv.is_locked && !isCancelled && <Btn data-testid="finalize-btn" onClick={finalize}><Lock size={16} /> Finalise</Btn>}
          {!isCancelled && <Btn data-testid="record-payment-btn" onClick={() => setPay(true)}><Plus size={16} /> Payments</Btn>}
          {!isCancelled && <Btn data-testid="cancel-invoice-btn" variant="ghost" onClick={() => (isPreview ? setHardDelOpen(true) : setCancelOpen(true))}><Ban size={16} /> Cancel</Btn>}
          <Btn data-testid="delete-invoice-btn" variant="danger" onClick={() => setHardDelOpen(true)}><Trash2 size={16} /> Delete</Btn>
        </div>} />

      {isPreview && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="preview-banner">
          <div className="text-sm text-amber-200"><b>Preview</b> — not an invoice yet and <b>no number is reserved</b>. Check the details, then <b>Finalise</b> to issue it (the next free number is assigned at that moment), <b>Edit</b> to change it, or <b>Cancel</b> to drop it.</div>
          <div className="flex gap-2">
            <Btn data-testid="preview-edit-btn" variant="ghost" onClick={() => nav(`/invoices/${id}/edit`)}><Pencil size={15} /> Edit</Btn>
            <Btn data-testid="preview-cancel-btn" variant="ghost" onClick={() => setHardDelOpen(true)}><Ban size={15} /> Cancel</Btn>
            <Btn data-testid="preview-finalize-btn" onClick={finalize}><Lock size={15} /> Finalise</Btn>
          </div>
        </div>
      )}

      <Card className="p-5 mb-4" data-testid="invoice-meta-card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-xs font-mono uppercase tracking-wider text-slate-500">Client</div><div className="text-slate-200 mt-1" data-testid="inv-detail-client">{(inv.client && inv.client.company_name) || inv.client_name_legacy || "—"}</div></div>
          <div><div className="text-xs font-mono uppercase tracking-wider text-slate-500">Invoice Date</div><div className="text-slate-200 mt-1 font-mono" data-testid="inv-detail-date">{fmtDate(inv.date)}</div></div>
          <div><div className="text-xs font-mono uppercase tracking-wider text-slate-500">Due Date</div><div className="text-slate-200 mt-1 font-mono" data-testid="inv-detail-due">{fmtDate(inv.due_date)}</div></div>
          <div><div className="text-xs font-mono uppercase tracking-wider text-slate-500">Status</div><div className="mt-1"><StatusBadge status={inv.balance <= 0 && inv.total > 0 ? "paid" : inv.paid_amount > 0 ? "partial" : inv.status} /></div></div>
        </div>
      </Card>

      {inv.client && (inv.client.address || inv.client.company_name || inv.client.gstin) && (
        <Card className="p-5 mb-4" data-testid="invoice-billto-card">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Bill To</div>
          <div className="text-slate-200 font-medium" data-testid="inv-detail-client-name">{inv.client.company_name || inv.client.name || inv.client_name_legacy || "—"}</div>
          {inv.client.address && <div className="text-slate-400 text-sm mt-1 whitespace-pre-line" data-testid="inv-detail-address">{inv.client.address.replace(/<\s*br\s*\/?\s*>/gi, "\n")}</div>}
          {(inv.client.state || inv.client.country) && (
            <div className="text-slate-400 text-sm">{[inv.client.state, inv.client.country].filter(Boolean).join(", ")}</div>
          )}
          {inv.client.gstin && <div className="text-slate-400 text-xs font-mono mt-1">GSTIN: {inv.client.gstin}</div>}
        </Card>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        {isCancelled && <Badge tone="rose">CANCELLED</Badge>}
        {inv.is_locked && !isCancelled && <Badge tone="sky"><Lock size={11} className="inline mr-1" />IMMUTABLE</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead className="bg-slate-900/40"><tr><Th>Description</Th><Th>HSN/SAC</Th><Th>Qty</Th><Th>Rate</Th><Th>Amount</Th></tr></thead>
            <tbody>{inv.items.map((it, i) => (
              <tr key={i} className="border-t border-slate-800/60">
                <Td>{it.description}</Td><Td className="font-mono text-xs">{it.hsn_sac || "—"}</Td>
                <Td className="font-mono">{it.qty}</Td><Td className="font-mono">{money(it.rate, inv.currency)}</Td>
                <Td className="font-mono">{money(it.amount, inv.currency)}</Td>
              </tr>))}</tbody>
          </table>
        </Card>
        <Card className="p-5">
          <div className="font-mono text-sm space-y-2">
            <Row label="Subtotal" value={money(inv.subtotal, inv.currency)} />
            {inv.cgst > 0 && <Row label={`CGST${oneRate != null ? ` @ ${pct(oneRate / 2)}%` : ""}`} value={money(inv.cgst, inv.currency)} />}
            {inv.sgst > 0 && <Row label={`SGST${oneRate != null ? ` @ ${pct(oneRate / 2)}%` : ""}`} value={money(inv.sgst, inv.currency)} />}
            {inv.igst > 0 && <Row label={`IGST${oneRate != null ? ` @ ${pct(oneRate)}%` : ""}`} value={money(inv.igst, inv.currency)} />}
            {inv.round_off ? <Row label="Round off" value={`${inv.round_off > 0 ? "+" : "−"}${money(Math.abs(inv.round_off), inv.currency)}`} /> : null}
            <div className="border-t border-slate-700 my-2" />
            <div className="flex justify-between text-lg"><span className="text-slate-300">Total</span><span className="text-slate-100 font-bold">{money(inv.total, inv.currency)}</span></div>
            <Row label="Paid" value={money(inv.paid_amount, inv.currency)} />
            <div className="flex justify-between"><span className="text-slate-400">Balance</span><span className="text-amber-400 font-bold">{money(inv.balance, inv.currency)}</span></div>
          </div>
        </Card>
      </div>

      <Card className="mt-6 overflow-hidden md:overflow-visible">
        <div className="px-5 py-3 border-b border-slate-800 font-heading font-semibold text-slate-200">Payments</div>
        <div className="overflow-x-auto md:overflow-visible"><table className="w-full min-w-[720px]"><thead className="bg-slate-900/40"><tr><Th>Date</Th><Th>Received</Th><Th>TDS recoverable</Th><Th>Applied</Th><Th>Method</Th><Th>Reference</Th><Th>By</Th></tr></thead>
          <tbody>{(inv.payments || []).map((p) => (
            <tr key={p.id} className="border-t border-slate-800/60"><Td className="text-slate-400">{fmtDate(p.date)}</Td>
              <Td className="font-mono text-emerald-400">{money(p.amount, inv.currency)}</Td>
              <Td className="font-mono text-amber-300">{p.tds ? money(p.tds, inv.currency) : "—"}</Td>
              <Td className="font-mono text-slate-200">{money((Number(p.amount) || 0) + (Number(p.tds) || 0), inv.currency)}</Td>
              <Td>{p.method}</Td>
              <Td className="font-mono text-xs">{p.reference || "—"}</Td><Td className="text-slate-500 text-xs">{p.created_by}</Td></tr>))}
            {(inv.payments || []).length === 0 && <tr><Td className="text-slate-500" colSpan={7}>No payments recorded.</Td></tr>}
          </tbody></table></div>
      </Card>

      <Modal open={pay} onClose={() => setPay(false)} title="Payments">
        {(inv.payments || []).length > 0 && (
          <div className="mb-4 rounded-lg border border-slate-700 overflow-hidden" data-testid="pay-modal-history">
            <div className="px-3 py-2 bg-slate-900/60 text-xs font-mono uppercase tracking-wider text-slate-400 flex justify-between">
              <span>Already recorded ({inv.payments.length})</span>
              <span>Paid {money(inv.paid_amount ?? inv.payments.reduce((s, p) => s + (Number(p.amount) || 0) + (Number(p.tds) || 0), 0), inv.currency)} · Balance <span className={inv.balance > 0.5 ? "text-amber-300" : "text-emerald-400"}>{money(inv.balance ?? 0, inv.currency)}</span></span>
            </div>
            <table className="w-full text-sm"><thead><tr className="text-xs text-slate-500"><Th className="!static">Date</Th><Th className="!static">Received</Th><Th className="!static">TDS</Th><Th className="!static">Method</Th><Th className="!static">Ref</Th><Th className="!static"></Th></tr></thead>
              <tbody>{inv.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-800/60" data-testid={`pay-modal-row-${p.id}`}>
                  <Td className="text-slate-400 text-xs">{fmtDate(p.date)}</Td>
                  <Td className="font-mono text-emerald-400 text-xs">{money(p.amount, inv.currency)}</Td>
                  <Td className="font-mono text-amber-300 text-xs">{p.tds ? money(p.tds, inv.currency) : "—"}</Td>
                  <Td className="text-xs">{p.method}</Td><Td className="font-mono text-xs">{p.reference || "—"}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <button data-testid={`edit-payment-${p.id}`} title="Edit payment" onClick={() => startEditPay(p)} className="text-slate-400 hover:text-sky-300 mr-2"><Pencil size={13} /></button>
                    <button data-testid={`delete-payment-${p.id}`} title="Delete payment" onClick={() => deletePay(p)} className="text-slate-400 hover:text-rose-400"><Trash2 size={13} /></button></Td></tr>))}
              </tbody></table>
          </div>
        )}
        <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 flex items-center justify-between">
          <span>{editPayId ? "Edit payment" : "Record new payment"}</span>
          {editPayId && <button data-testid="cancel-edit-payment" onClick={cancelEditPay} className="text-slate-400 hover:text-slate-200 normal-case tracking-normal">cancel edit</button>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Amount Received</Label><Input data-testid="pay-amount" type="number" value={pf.amount} onChange={(e) => setPf({ ...pf, amount: e.target.value })} /></div>
          <div><Label>Date</Label><Input data-testid="pay-date" type="date" value={pf.date} onChange={(e) => setPf({ ...pf, date: e.target.value })} /></div>
          <div><Label>TDS Deducted (recoverable)</Label><Input data-testid="pay-tds" type="number" value={pf.tds} onChange={(e) => setPf({ ...pf, tds: e.target.value })} placeholder="0" /></div>
          <div><Label>Method</Label><Select data-testid="pay-method" value={pf.method} onChange={(e) => setPf({ ...pf, method: e.target.value })}>
            {["bank_transfer", "upi", "cash", "cheque", "card", "paypal"].map((m) => <option key={m}>{m}</option>)}</Select></div>
          <div className="col-span-2"><Label>Reference</Label><Input data-testid="pay-ref" value={pf.reference} onChange={(e) => setPf({ ...pf, reference: e.target.value })} /></div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-800/60 border border-slate-700 px-4 py-2.5" data-testid="pay-applied-row">
          <span className="text-sm text-slate-400">Applied to invoice <span className="text-xs text-slate-500">(received + TDS)</span></span>
          <span className="font-mono font-bold text-emerald-400" data-testid="pay-applied-total">{money((Number(pf.amount) || 0) + (Number(pf.tds) || 0), inv.currency)}</span>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setPay(false)}>Cancel</Btn>
          <Btn data-testid="save-payment-btn" onClick={editPayId ? saveEditPay : addPay}>{editPayId ? "Update Payment" : "Save Payment"}</Btn></div>
      </Modal>

      <Modal open={emailOpen} onClose={() => setEmailOpen(false)} title="Email Invoice PDF">
        <p className="text-slate-400 text-sm mb-3">Sends this invoice as a PDF attachment via the IWS mail server.</p>
        <Label>Recipient email</Label>
        <Input data-testid="email-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@example.com" />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Btn>
          <Btn data-testid="send-email-btn" disabled={sending} onClick={sendEmail}><Mail size={16} /> {sending ? "Sending…" : "Send"}</Btn></div>
      </Modal>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel Invoice" danger>
        <p className="text-slate-300 text-sm mb-4">This marks <b>{inv.invoice_number}</b> as <b>CANCELLED</b>. The record is kept for audit but excluded from receivables, aging and revenue. This is logged.</p>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setCancelOpen(false)}>Keep Invoice</Btn>
          <Btn data-testid="confirm-cancel-btn" variant="danger" onClick={cancelInvoice}><Ban size={16} /> Cancel Invoice</Btn></div>
      </Modal>

      <Modal open={hardDelOpen} onClose={() => setHardDelOpen(false)} title={isPreview ? "Discard Preview" : "Delete Invoice Permanently"} danger>
        {isPreview
          ? <p className="text-slate-300 text-sm mb-4">This preview has <b>no invoice number</b> and is not an invoice yet. Discarding it removes it completely — nothing is left in the series and no number is consumed.</p>
          : <p className="text-slate-300 text-sm mb-4">This <b>permanently deletes</b> {inv.invoice_number} and all its recorded payments. This cannot be undone and is logged in the audit trail. Only the <b>most recent invoice</b> in a numbering series can be deleted — if a later invoice exists, deletion is blocked to keep the sequence gap-free.</p>}
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setHardDelOpen(false)}>Keep</Btn>
          <Btn data-testid="confirm-delete-btn" variant="danger" onClick={hardDelete}><Trash2 size={16} /> {isPreview ? "Discard Preview" : "Delete Permanently"}</Btn></div>
      </Modal>
    </div>
  );
}
